import { DEFAULT_BRAND } from "@shared/brand.types";
import type { EcoleActivite, EcoleConfig, EcoleSchool } from "@shared/ecole.types";
import { Briefcase, LayoutTemplate, LogOut, Users } from "lucide-react";
import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { BrandRoot } from "@/components/BrandRoot";
import { Button } from "@/components/ds/Button";
import { type EntreeNav, EspaceLayout } from "@/components/EspaceLayout";
import { OtpGate } from "@/components/OtpGate";
import { PageMessage } from "@/components/PageMessage";
import { fetchJson } from "@/lib/http";
import { ConfigPage } from "./ConfigPage";
import { A_TRAITER } from "./labels";
import { NouvelleSelectionPage } from "./NouvelleSelectionPage";
import { OffresPage } from "./OffresPage";
import { SelectionsPage } from "./SelectionsPage";
import { useActivite } from "./useActivite";
import { useEcole } from "./useEcole";

/**
 * L'espace école : la troisième porte, et ses trois écrans derrière elle.
 *
 * Un référent y entre comme une entreprise entre dans le sien — un email, un code,
 * trente jours. C'est la même règle, et elle ne s'arrête pas à l'entreprise : il
 * n'y a pas de mot de passe ici non plus.
 *
 * La coque est celle de l'espace entreprise (`EspaceLayout`), aux couleurs de
 * l'école COURANTE. C'est délibéré : le référent règle son aplat dans « Ma page »,
 * et la barre qu'il a sous les yeux est celle qu'il vient de choisir, celle que
 * ses entreprises verront.
 */
export function EcolePage() {
	const { state, school, choose, saveConfig, saveError, reload } = useEcole();

	if (state.status === "loading") {
		return (
			<div className="flex min-h-screen items-center justify-center bg-mg-page">
				<p className="t-body text-mg-secondary">Chargement…</p>
			</div>
		);
	}

	if (state.status === "anonymous") {
		return (
			<BrandRoot brand={DEFAULT_BRAND} className="min-h-screen bg-mg-subtle">
				<OtpGate
					texts={{
						eyebrow: "Espace école",
						title: "Offres reçues et profils envoyés",
						emailLabel: "Votre email",
						emailHelp: "Votre adresse email professionnelle.",
						emailPlaceholder: "prenom.nom@ecole.fr",
						askCode: "Recevoir un code par mail",
						enter: "Ouvrir mon espace",
					}}
					requestPath="/api/ecole/access/request"
					verifyPath="/api/ecole/access/verify"
					onOpen={reload}
				/>
			</BrandRoot>
		);
	}

	if (state.status === "failed" || !school) {
		return (
			<PageMessage
				title="Votre espace n'a pas pu s'ouvrir"
				body="Le service ne répond pas pour le moment. Réessayez dans un instant."
				retry={{ label: "Réessayer", onClick: () => window.location.reload() }}
			/>
		);
	}

	const { admin, schools } = state.overview;

	return (
		<BrandRoot brand={school}>
			{/*
			 * ⚠️ La `key` n'est pas cosmétique : elle REMONTE l'écran à chaque
			 * changement d'école.
			 *
			 * Les champs s'initialisent une seule fois (`useFieldSave` et
			 * `FormFieldsEditor` prennent leur valeur de départ dans un `useState`,
			 * qui ne se resynchronise jamais sur une prop). Sans elle, un référent de
			 * groupe qui bascule sur sa deuxième école garde à l'écran les valeurs de
			 * la première — et le premier champ qu'il quitte les ÉCRIT sur la
			 * nouvelle, puisque `brandId`, lui, a bien changé.
			 */}
			<EcoleEspace
				key={school.brandId}
				school={school}
				schools={schools}
				email={admin.email}
				onChoose={choose}
				onSaveConfig={saveConfig}
				saveError={saveError}
				onSessionFinie={reload}
			/>
		</BrandRoot>
	);
}

async function logout() {
	// Un logout qui échoue ne retient personne : le référent a demandé à sortir, et
	// la porte à code le reprendra de toute façon. Sans ce filet, une coupure réseau
	// levait dans un gestionnaire d'événement, donc en dehors de l'ErrorBoundary, et
	// le bouton restait sans effet visible.
	try {
		await fetchJson("/api/ecole/logout", { method: "POST" });
	} catch {
		// Rien à dire ici : la ligne suivante ramène à la porte dans les deux cas.
	}
	window.location.assign("/ecole");
}

/**
 * Le choix de l'école, affiché SEULEMENT quand il y en a plusieurs.
 *
 * 11 référents sont dans ce cas, sur 476 : les groupes IGENSIA, Groupe Alternance
 * et ACE. Un menu à une seule entrée n'aiderait personne, et ferait douter les 465
 * autres qu'ils sont au bon endroit.
 *
 * Il vit sur sa propre rangée, sous le nom de l'école, pleine largeur de la barre.
 * À la place du nom, un menu de 168 px coupait « École supérieure… » net : le
 * référent ne lisait plus où il était. Posé dans le bandeau, il se serrait contre
 * le nom tronqué et le bouton de sortie en 390.
 */
function SchoolPicker({
	schools,
	current,
	onChoose,
}: {
	schools: EcoleSchool[];
	current: EcoleSchool;
	onChoose: (brandId: string) => void;
}) {
	return (
		<label className="block">
			<span className="t-caption mb-1 block text-mg-nav-fg-muted">Changer d'école</span>
			<select
				className="mg-field min-h-11 w-full md:min-h-0"
				value={current.brandId}
				onChange={(event) => onChoose(event.target.value)}
			>
				{schools.map((school) => (
					<option key={school.brandId} value={school.brandId}>
						{school.schoolName}
					</option>
				))}
			</select>
		</label>
	);
}

/**
 * Les trois écrans de l'école, leur coque et leurs sous-routes.
 *
 * Remonté à chaque changement d'école (la `key` posée par l'appelant) : l'activité
 * se relit pour la nouvelle école, et les champs de configuration repartent de ses
 * valeurs. Les routes sont RELATIVES : `App` déclare `/ecole/*`, et c'est ici
 * qu'elles vivent, avec la coque qui les entoure.
 */
function EcoleEspace({
	school,
	schools,
	email,
	onChoose,
	onSaveConfig,
	saveError,
	onSessionFinie,
}: {
	school: EcoleSchool;
	schools: EcoleSchool[];
	email: string;
	onChoose: (brandId: string) => void;
	onSaveConfig: (patch: EcoleConfig) => Promise<boolean>;
	saveError: string | null;
	onSessionFinie: () => void;
}) {
	const activite = useActivite(school.brandId, onSessionFinie);
	const { state } = activite;
	const aTraiter =
		state.status === "ready"
			? state.activite.offers.filter((offer) => A_TRAITER.includes(offer.status)).length
			: 0;

	const ready = state.status === "ready" ? state.activite : null;
	const avecActivite = (rendu: (a: EcoleActivite) => ReactNode) =>
		ready ? rendu(ready) : <ChargementActivite state={state} onRetry={activite.reload} />;

	const entrees: EntreeNav[] = [
		{
			to: "/ecole",
			end: true,
			label: "Offres reçues",
			shortLabel: "Offres",
			icon: Briefcase,
			badge: aTraiter,
		},
		{ to: "/ecole/profils", label: "Profils envoyés", shortLabel: "Profils", icon: Users },
		{ to: "/ecole/page", label: "Ma page de dépôt", shortLabel: "Ma page", icon: LayoutTemplate },
	];

	return (
		<EspaceLayout
			navLabel="Espace école"
			logoUrl={school.logoUrl}
			name={school.schoolName}
			caption="Espace école"
			picker={
				schools.length > 1 ? (
					<SchoolPicker schools={schools} current={school} onChoose={onChoose} />
				) : undefined
			}
			entries={entrees}
			user={<span className="t-label truncate text-mg-heading">{email}</span>}
			actions={
				<Button
					tone="tertiary"
					size="sm"
					onClick={logout}
					aria-label="Fermer la session"
					icon={<LogOut className="size-4" />}
				/>
			}
			alert={
				/*
				 * Le motif de l'échec, que le champ ne peut pas porter.
				 *
				 * « Non enregistré » s'affiche bien à côté du champ, mais il disparaît au
				 * bout de six secondes et ne dit pas ce qui s'est passé. Ce qui s'écrit
				 * ici devient la page publique de l'école : elle doit savoir que sa
				 * dernière modification n'y est pas, et pourquoi.
				 */
				saveError ? (
					<p role="alert" className="t-body bg-mg-error-subtle px-5 py-3 text-mg-error-fg">
						Votre dernière modification n'a pas été enregistrée. {saveError}
					</p>
				) : null
			}
		>
			<Routes>
				<Route
					index
					element={avecActivite((a) => <OffresPage offers={a.offers} actions={activite} />)}
				/>
				<Route
					path="profils"
					element={avecActivite((a) => <SelectionsPage selections={a.selections} />)}
				/>
				<Route
					path="profils/nouvelle"
					element={avecActivite((a) => (
						<NouvelleSelectionPage offers={a.offers} envoyer={activite.envoyer} />
					))}
				/>
				<Route path="page" element={<ConfigPage school={school} onSave={onSaveConfig} />} />
				<Route path="*" element={<Navigate to="/ecole" replace />} />
			</Routes>
		</EspaceLayout>
	);
}

function ChargementActivite({
	state,
	onRetry,
}: {
	state: { status: string; message?: string };
	onRetry: () => void;
}) {
	if (state.status === "failed") {
		return (
			<div className="flex flex-col items-start gap-3">
				<p role="alert" className="t-body text-mg-error-fg">
					{state.message}
				</p>
				<Button tone="primary" onClick={onRetry}>
					Réessayer
				</Button>
			</div>
		);
	}
	return <p className="t-body text-mg-secondary">Chargement…</p>;
}
